import type { FinancingResult } from "../../shared/contracts";
import { currency, systemLabel } from "../lib/format";

type ChartMetric = "firstPayment" | "totalPaid";

interface FoundationChartProps {
  results: FinancingResult[];
  selectedKey: string;
  metric: ChartMetric;
  onMetricChange: (metric: ChartMetric) => void;
  onSelect: (key: string) => void;
}

export function FoundationChart({
  results,
  selectedKey,
  metric,
  onMetricChange,
  onSelect
}: FoundationChartProps) {
  const values = results.map((result) => result[metric]);
  const maximum = Math.max(...values);
  const minimum = Math.min(...values);
  const spread = Math.max(1, maximum - minimum);

  return (
    <section className="foundation" aria-labelledby="foundation-title">
      <div className="foundation__header">
        <div>
          <span className="section-kicker">03 · Régua de fundações</span>
          <h2 id="foundation-title">Veja o peso de cada estrutura</h2>
        </div>
        <div className="segmented-control" aria-label="Métrica do gráfico">
          <button
            type="button"
            aria-pressed={metric === "firstPayment"}
            onClick={() => onMetricChange("firstPayment")}
          >
            Parcela inicial
          </button>
          <button
            type="button"
            aria-pressed={metric === "totalPaid"}
            onClick={() => onMetricChange("totalPaid")}
          >
            Custo total
          </button>
        </div>
      </div>

      <div className="foundation__chart" role="list" aria-label="Comparação visual das opções">
        {results.map((result) => {
          const normalized = (result[metric] - minimum) / spread;
          const height = 34 + normalized * 58;
          return (
            <button
              key={result.key}
              type="button"
              role="listitem"
              className={`foundation-bar ${selectedKey === result.key ? "is-selected" : ""} ${
                result.eligibility.eligible ? "" : "is-ineligible"
              }`}
              onClick={() => onSelect(result.key)}
              aria-label={`${result.bankShortName} ${systemLabel(result.system)}: ${currency(
                result[metric]
              )}${result.eligibility.eligible ? "" : ", fora dos critérios"}`}
            >
              <span className="foundation-bar__value">{currency(result[metric])}</span>
              <span
                className={`foundation-bar__column foundation-bar__column--${result.system}`}
                style={{
                  height: `${height}%`,
                  backgroundColor: result.accent
                }}
                aria-hidden="true"
              >
                <i />
              </span>
              <span className="foundation-bar__label">
                <strong>{result.bankShortName}</strong>
                <small>{systemLabel(result.system)}</small>
              </span>
            </button>
          );
        })}
        <span className="foundation__baseline" aria-hidden="true" />
      </div>

      <div className="chart-legend">
        <span>
          <i className="legend-solid" /> SAC
        </span>
        <span>
          <i className="legend-striped" /> Price
        </span>
        <span>
          <i className="legend-muted" /> fora dos critérios
        </span>
      </div>
    </section>
  );
}

